import moment from 'moment';
import { ProductionLineSummary } from '../libs/GraphQL';

/**
 * Normalize error messages by extracting the base message (removing order IDs)
 * @param {string} fullErrorMessage - The complete error message
 * @returns {string} The normalized base error message
 */
export function normalizeErrorMessage(fullErrorMessage) {
  if (!fullErrorMessage) return '';

  // Extract base error message by removing order ID pattern
  // Pattern: "Cannot delete order [UUID or ID]: [rest of message]"
  return (
    fullErrorMessage.replace(/Cannot delete order [\w-]+: /, '').trim() ||
    fullErrorMessage
  );
}

/**
 * Enrich order data with display-friendly information
 * @param {Object} order - The order object
 * @returns {Object} Order data with additional display properties
 */
export function enrichOrderForDisplay(order) {
  if (!order) return {};

  return {
    ...order,
    locationId: order.locationId || 'Unknown Location',
    deliveryDate: order.deliveryDate || order.dropOffDate || 'Unknown Date',
  };
}

/**
 * Get today's date in Chicago timezone
 * @returns {string} Date in YYYY-MM-DD format
 */
export function getTodayInChicagoTimezone() {
  const chicagoDate = new Date(
    new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }),
  );
  return moment(chicagoDate).format('YYYY-MM-DD');
}

/**
 * Validate orders before deletion by checking for received items
 * @param {Object|Array} orders - Single order object or array of order objects
 * @returns {Promise<Array|null>} Array of conflicts or null if no conflicts
 */
export async function validateOrdersForReceivedItems(orders) {
  try {
    const today = getTodayInChicagoTimezone();

    // Normalize input to always be an array
    const ordersArray = Array.isArray(orders) ? orders : [orders];

    // Extract items with production date === today
    const itemsWithTodayProduction = ordersArray.flatMap((order) => {
      if (!order.itemsCount || order.itemsCount.length === 0) {
        return [];
      }
      return order.itemsCount
        .filter((item) => item.productionDate === today)
        .map((item) => {
          // Handle both 'count' and 'quantity' field names
          const quantity = item.count || item.quantity;
          return {
            orderId: order.uniqueId,
            itemName: item.itemName,
            productionDate: item.productionDate,
            locationId: order.wholesaleLocationId || order.locationId,
            dropOffDate: order.dropOffDate,
            count: quantity,
          };
        });
    });

    // If no items with today's production date, no conflicts possible
    // Production date in the future we do not have received counts yet, so we do not block deletion
    if (itemsWithTodayProduction.length === 0) {
      return null;
    }

    // Query ProductionLineSummary for today
    const productionSummary = await ProductionLineSummary(today, 'CHICAGO_1');

    // Filter for RECEIVED state
    const receivedItems =
      productionSummary.data.productionLineItemSummary.filter(
        (item) => item.state === 'RECEIVED',
      );

    // Build a Set of received item names for fast lookup
    const receivedItemNames = new Set(
      receivedItems.map((item) => item.itemName),
    );

    // Check for conflicts - group by orderId to keep received items separate per order
    const conflicts = {};
    itemsWithTodayProduction.forEach((item) => {
      if (receivedItemNames.has(item.itemName)) {
        if (!conflicts[item.orderId]) {
          conflicts[item.orderId] = {
            locationId: item.locationId,
            dropOffDate: item.dropOffDate,
            orderId: item.orderId,
            receivedItems: [],
          };
        }
        conflicts[item.orderId].receivedItems.push({
          itemName: item.itemName,
          productionDate: item.productionDate,
          count: item.count,
        });
      }
    });

    // Return conflicts if any found
    // Production date of today and items have been picked, so we block deletion
    if (Object.keys(conflicts).length > 0) {
      return Object.values(conflicts);
    }

    return null;
  } catch (error) {
    console.error('Error validating orders:', error);
    throw error;
  }
}
